import React, { useMemo } from 'react';
import { Result, Button, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { getRbacMode } from '../rbac/mode';
import {
  canonicalizeRole,
  canonicalizeRoles,
  isPlatformAdmin,
  type CanonicalRole,
} from '../rbac/roles';

interface RoleGuardProps {
  /** Legacy role allowlist. Still consulted in `legacy` and `dual` modes. */
  allowedRoles: string[];
  /**
   * Wave A canonical permissions to require. When provided the guard prefers
   * a Wave A decision (in `wave-a` and `dual` modes).
   */
  permissions?: string[];
  /** Resource id for the permission checks (when applicable). */
  resourceId?: string;
  /** When true, all `permissions` must be granted. Defaults to false (any). */
  requireAll?: boolean;
  /**
   * When true (default), master_admin / super_admin always pass through the
   * legacy code path. Set to false only for the rare case a guard genuinely
   * needs to keep Janus internal staff out (e.g., a tenant-only feature flag).
   * Wave 3 gap #2 FE.
   */
  bypassPlatformAdmin?: boolean;
  children: React.ReactNode;
}

/**
 * Route guard with dual-mode RBAC support (Wave A) layered over the
 * canonical role registry (Wave 3).
 *
 * Modes (resolved from `getRbacMode()`):
 *   - `legacy` (default): canonicalizes `allowedRoles` and the user's role(s)
 *                         via the Wave 3 alias map; platform admins bypass
 *                         (gap #2 FE) unless `bypassPlatformAdmin={false}`.
 *   - `wave-a`: ignores `allowedRoles`; requires `permissions` and uses
 *               `usePermissions` for each. If no `permissions` prop is
 *               supplied, falls back to the legacy (Wave 3) decision so
 *               partially-migrated routes don't break.
 *   - `dual`  : evaluates both; logs a discrepancy via `console.warn` (and
 *               PostHog if available); prefers the Wave A decision when
 *               `permissions` is supplied.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
  allowedRoles,
  permissions,
  resourceId,
  requireAll = false,
  bypassPlatformAdmin = true,
  children,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mode = getRbacMode();

  // Always call hooks unconditionally to satisfy rules-of-hooks; the result is
  // ignored in `legacy` mode.
  const { has, loading: permsLoading } = usePermissions();

  const allowedSet = useMemo<Set<CanonicalRole>>(
    () => new Set(canonicalizeRoles(allowedRoles)),
    [allowedRoles],
  );

  if (!user) return null;

  // ---- Legacy decision (Wave 3 canonicalized) ------------------------------
  // IMPORTANT: account_role lives on the account_membership claim, NOT in the
  // tenant-scoped roles[] / role fields. We MUST include it here so routes
  // that gate on `account_owner` (e.g. /admin/billing, /admin/estrutura,
  // /rollup) authorize Diana — her tenant role is "admin", account_role is
  // "account_owner", and without this both Account-Admin sidebar entries
  // failed the guard.
  const u = user as unknown as Record<string, unknown>;
  const accountRole =
    (u.accountRole as string | undefined) ?? (u.account_role as string | undefined);
  const userRoleStrings: string[] = [
    ...((u.roles as string[] | undefined) ?? []),
    user.role,
    accountRole,
  ].filter(Boolean) as string[];

  const platformAdminBypass = bypassPlatformAdmin && isPlatformAdmin(userRoleStrings);
  if (platformAdminBypass) {
    return <>{children}</>;
  }

  const userCanonical = canonicalizeRoles(userRoleStrings);
  const userPrimary = canonicalizeRole(user.role);
  const legacyAllowed =
    userCanonical.some((r) => allowedSet.has(r)) ||
    (userPrimary != null && allowedSet.has(userPrimary));

  // ---- Wave A decision (permissions) ---------------------------------------
  const hasPermissionsProp = !!permissions && permissions.length > 0;
  let waveAAllowed: boolean | null = null;
  if (hasPermissionsProp) {
    if (permsLoading) {
      waveAAllowed = null; // unknown
    } else {
      waveAAllowed = requireAll
        ? permissions!.every((p) => has(p, resourceId))
        : permissions!.some((p) => has(p, resourceId));
    }
  }

  // ---- Compose final decision based on mode --------------------------------
  let allowed: boolean;
  switch (mode) {
    case 'wave-a': {
      if (hasPermissionsProp) {
        if (waveAAllowed === null) {
          // permissions still loading — show spinner instead of forbidden
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
              }}
            >
              <Spin size="large" />
            </div>
          );
        }
        allowed = waveAAllowed;
      } else {
        allowed = legacyAllowed;
      }
      break;
    }
    case 'dual': {
      if (hasPermissionsProp && waveAAllowed !== null && waveAAllowed !== legacyAllowed) {
        // Discrepancy — log for telemetry. Prefer Wave A.
        // eslint-disable-next-line no-console
        console.warn('[rbac.dual_mode.discrepancy]', {
          permissions,
          resourceId,
          legacy: legacyAllowed,
          waveA: waveAAllowed,
          path: typeof window !== 'undefined' ? window.location.pathname : null,
        });
        // Best-effort PostHog telemetry without coupling to the provider.
        try {
          const ph = (
            globalThis as unknown as {
              posthog?: {
                capture?: (event: string, properties?: Record<string, unknown>) => void;
              };
            }
          ).posthog;
          ph?.capture?.('rbac.dual_mode.discrepancy', {
            permissions,
            resourceId,
            legacy: legacyAllowed,
            waveA: waveAAllowed,
            path: typeof window !== 'undefined' ? window.location.pathname : null,
          });
        } catch {
          // never throw from telemetry path
        }
      }
      // In dual mode, prefer Wave A when known; else fall back to legacy.
      if (hasPermissionsProp && waveAAllowed !== null) {
        allowed = waveAAllowed;
      } else {
        allowed = legacyAllowed;
      }
      break;
    }
    case 'legacy':
    default:
      allowed = legacyAllowed;
      break;
  }

  if (!allowed) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <Result
          icon={<ShieldX size={64} color="#EF4444" />}
          status="403"
          title="Acesso restrito"
          subTitle="Você não tem permissão para acessar esta página. Entre em contato com o administrador da sua empresa."
          extra={
            <Button type="primary" onClick={() => navigate('/')}>
              Voltar ao Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default RoleGuard;
